import { usePurchaseHistory } from '@/hooks/usePurchaseHistory';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { formatDateWithYear } from '@/utils/dateFormatters';
import BreadcrumbComponent from '@/components/BreadcrumbComponent';
import { History, FileSpreadsheet, CircleCheck, ChevronDown, ChevronUp } from 'lucide-react';
import CardSkeleton from '@/components/CardSkeleton';
import PurchaseHistoryStub from '@/components/PurchaseHistoryStub';
import { Typography } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';

const PurchaseHistory = () => {
  const { purchaseData, isLoading, showMore, setShowMore } = usePurchaseHistory();
  return (
    <div className="flex flex-col p-3 w-full">
      <BreadcrumbComponent link={'Последние оплаты'} />
      <div className="mb-6 flex items-center gap-2">
        <History className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-medium text-gray-900">Последние оплаты</h2>
      </div>
      <div className="grid gap-4 m:grid-cols-1">
        {!isLoading && (!purchaseData || purchaseData.length === 0) && <PurchaseHistoryStub />}
      </div>
      <div className="grid gap-4 sm:grid-cols-1">
        {isLoading
          ? [...Array(3)].map((_, index) => <CardSkeleton key={index} />)
          : purchaseData &&
            purchaseData.slice(0, showMore ? purchaseData.length : 3).map((purchase) =>
              purchase.products[0].price !== 0 ? (
                <Card
                  key={purchase.id}
                  className="max-w-[60rem] overflow-hidden transition-all hover:shadow-lg">
                  <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-primary/10 p-4">
                    <div className="flex flex-col gap-2">
                      {purchase.products.map((productItem) => (
                        <div key={productItem.product.id} className="group flex items-center gap-3">
                          <div className="rounded-full bg-primary/10 p-2 sm:p-2">
                            <CircleCheck className="h-5 w-5 text-primary min-w-[20px]" />
                          </div>
                          <Typography
                            variant="h3"
                            className="text-lg font-normal text-gray-800 font-normal group-hover:text-primary transition-colors">
                            {productItem.product.title}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  </CardHeader>

                  {/* Контент */}
                  <CardContent className="space-y-4 p-4">
                    {/* Сумма оплаты */}
                    <div className="flex items-center justify-between">
                      <Typography variant="muted" className="text-muted-foreground">
                        Сумма:
                      </Typography>
                      <div className="inline-flex items-center">
                        <Typography variant="p" className="text-base leading-relaxed">
                          {purchase.total_amount} ₽
                        </Typography>
                      </div>
                    </div>

                    {/* Дата оплаты */}
                    <div className="flex items-center justify-between text-sm">
                      <Typography variant="muted" className="text-muted-foreground">
                        Дата оплаты:
                      </Typography>
                      <Typography variant="p" className="text-base leading-relaxed">
                        {formatDateWithYear(purchase.datetime)}
                      </Typography>
                    </div>

                    {/* Детали продуктов, если их больше одного */}
                    {purchase.products.length > 1 && (
                      <div className="flex flex-col gap-2 pt-2 border-t">
                        <span className="text-sm text-muted-foreground">Состав покупки:</span>
                        <div className="flex flex-col gap-1.5">
                          {purchase.products.map((item) => (
                            <div
                              key={item.product.id}
                              className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">{item.product.title}</span>
                              <span className="font-medium">{item.amount} ₽</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Статус оплаты */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <span className="text-sm text-muted-foreground">Оплачено</span>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <PurchaseHistoryStub />
              ),
            )}

        {purchaseData && purchaseData.length > 3 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full max-w-md mx-auto mb-4 bg-white/80 backdrop-blur-sm border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 transition-all duration-200 shadow-sm hover:shadow-md"
            onClick={() => {
              setShowMore(!showMore);
            }}>
            <span className="flex items-center gap-2">
              {showMore ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Скрыть
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Показать за более ранние даты
                </>
              )}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default PurchaseHistory;
